// src/OTPVerification.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './otp.css';
import { verifyOTP, resendOTP } from '../../api/api.js'; // Import the signIn API function
import Notification from '../../components/notification/notification.js'; // Adjust the path as needed

const OTPVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [errors, setErrors] = useState('');
  const [isResent, setIsResent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0); // 60 seconds countdown
  const [notification, setNotification] = useState(null); // Notification state

  const location = useLocation();
  const email = location.state?.email;

  const navigate = useNavigate(); // For programmatic navigation if needed

  // Create refs for each input field
  const inputRefs = useRef([]);

  // Handle countdown timer
  useEffect(() => {
    // Do not set up the timer if timeLeft is 0
    if (timeLeft <= 0) return;

    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    // Cleanup the interval on component unmount or when timeLeft changes
    return () => clearInterval(timerId);
  }, [timeLeft]);

  // Handle input change
  const handleChange = (e, index) => {
    const { value } = e.target;
    if (!/^[0-9]?$/.test(value)) return; // Allow only single numeric character

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next input if value is entered and not the last input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Clear errors when user starts typing
    if (errors) setErrors('');
  };

  // Handle key down events (e.g., backspace)
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && otp[index] === '') {
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const pasteOtp = pasteData.split('');
      setOtp(pasteOtp);
      // Populate the input fields
      pasteOtp.forEach((digit, idx) => {
        inputRefs.current[idx].value = digit;
      });
      // Focus the last input field
      inputRefs.current[5].focus();
      setErrors('');
    } else {
      setErrors('Invalid OTP format. Please enter 6 digits.');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join('');

    // Simple validation: Check if all fields are filled
    if (enteredOtp.length < 6) {
      setErrors('Please enter the 6-digit OTP.');
      return;
    }

    if (email) {
      try {

        const data = await verifyOTP({ email: email, code: enteredOtp });

        if (data.status === 200) {
          setNotification({ type: 'success', message: 'OTP verified successfully' });
          navigate('/generate');
        }

      } catch (error) {
        console.log(error.response.status)
        if (error.response.status === 400) {
          setNotification({ type: 'error', message: 'Invalid code' });
          // Only reset OTP fields on invalid code
          setOtp(['', '', '', '', '', '']);
          inputRefs.current[0].focus();
        }
        else if (error.response.status === 404) {
          setNotification({ type: 'error', message: 'Invalid code or user not found' });
        }
        else if (error.response.status === 410) {
          setNotification({ type: 'error', message: 'Verification code has expired' });
        }
        else {
          setNotification({ type: 'error', message: 'Failed to verify OTP' });
        }
      }
    }
  };

  // Handle resend OTP
  const handleResend = async () => {
    try {
      const data = await resendOTP({ email: email }); // Add await here
      if (data.status === 200) {
        setIsResent(true);
        setTimeLeft(60); // Reset timer
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0].focus();
        setNotification({ type: 'success', message: 'OTP resent successfully' });
      }

    } catch (error) {
      console.log(error.response.status);
      if (error.response.status === 404) {
        navigate('/');
      }
      else if (error.response.status === 429) {
        setNotification({ type: 'error', message: 'Too many requests. Please try again later' });
      }
      else if (error.response.status === 500) {
        setNotification({ type: 'error', message: 'Failed to resend OTP' });
      }
    }
  };

  // Add this useEffect to auto-clear notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000); // Clear notification after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div className="otp-container">
      {notification && (
        <div className="notification-wrapper">
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        </div>
      )}
      <form className="otp-form" onSubmit={handleSubmit}>
        <h2 className="otp-title">OTP Verification</h2>
        <p className="otp-description">Please enter the 6-digit code sent to your email.</p>

        <div className="otp-inputs" onPaste={handlePaste}>
          {otp.map((data, index) => (
            <input
              key={index}
              type="text"
              name={`otp-${index}`}
              id={`otp-${index}`}
              maxLength="1"
              value={data}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={(el) => (inputRefs.current[index] = el)}
              className={`otp-input ${errors ? 'otp-input-error' : ''}`}
              autoFocus={index === 0 ? true : false}
              aria-label={`OTP Digit ${index + 1}`}
              aria-describedby={errors ? 'otp-error' : undefined}
            />
          ))}
        </div>

        {errors && <span className="otp-error" id="otp-error">{errors}</span>}

        <button type="submit" className="otp-submit-button" disabled={otp.join('').length !== 6}>
          Verify OTP
        </button>

        <div className="otp-resend">
          <button
            type="button"
            className="otp-resend-button"
            onClick={handleResend}
            disabled={timeLeft > 0}
          >
            {timeLeft > 0 ? `Resend OTP in ${timeLeft}s` : 'Resend OTP'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OTPVerification;
